import { useQuery } from "@tanstack/react-query";
import { useTokensValues } from "../../../../../../../hooks/tokens";

interface ZipCodeValidationResult {
  status: boolean;
  state_name?: string;
  city_for_zipcode?: string;
  zip_code?: string;
}

interface ValidateZipCodeParams {
  zipcode: string;
  state?: string;
  endpoint?: string;
  authenticityToken: string;
}

export interface ZipCodeValidationOptions {
  apiEndpoint?: string;
}

const validateZipCode = async ({
  zipcode,
  state,
  endpoint = `${location.pathname}?step_action=verify_mailing_address`,
  authenticityToken,
}: ValidateZipCodeParams): Promise<ZipCodeValidationResult> => {
  const requestData: Record<string, string> = {
    authenticity_token: authenticityToken,
    mailing_us_zipcode: zipcode,
    mailing_us_state: state ?? "",
  };

  const response = await fetch(endpoint, {
    method: "POST",
    body: JSON.stringify(requestData),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as ZipCodeValidationResult;
};

interface UseZipCodeValidationParams {
  value: string | number | null | undefined;
  state?: string | undefined;
  validationOptions?: ZipCodeValidationOptions;
}

export const useZipCodeValidation = ({
  value,
  state,
  validationOptions,
}: UseZipCodeValidationParams) => {
  const tokens = useTokensValues();

  const query = useQuery({
    queryKey: ["zipcode-validation", value, state],
    queryFn: () => {
      const zipString = String(value ?? "");

      // If zip code is not exactly 5 characters, return valid result immediately (no API call)
      if (zipString.length !== 5) {
        return Promise.resolve({ status: true } as ZipCodeValidationResult);
      }

      // Only make API call for exactly 5-digit zip codes
      return validateZipCode({
        zipcode: zipString,
        state,
        endpoint: validationOptions?.apiEndpoint,
        authenticityToken: tokens.authenticityToken,
      });
    },
    enabled: !!value, // Always run when we have a value (even if it's short)
    staleTime: Infinity, // Cache forever as requested
    gcTime: Infinity, // Keep in cache forever
    retry: false, // Don't retry on error to avoid hammering the API
  });

  // Calculate validation state
  let isValid = true;
  let message: string | undefined;

  if (query.isSuccess && query.data) {
    if (
      query.data.status === false &&
      query.data.state_name &&
      query.data.zip_code
    ) {
      isValid = false;
      message = `Looks like that zip code is not in ${query.data.state_name}. Try updating your zip code.`;
    }
  } else if (query.isError) {
    // Don't fail the validation on network errors
    console.error("Zip code validation failed:", query.error);
    isValid = true;
  }

  return {
    ...query,
    isValid,
    validationMessage: message,
    city: query.data?.city_for_zipcode,
  };
};
